import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Chip,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import StoreIcon from '@mui/icons-material/Store';
import { useKeyword, useKeywordRankHistory } from '../../hooks';

export function KeywordDetailPage() {
  const { id } = useParams<{ id: string }>();
  const keywordId = Number(id);
  const navigate = useNavigate();

  const { keyword, isLoading: isKeywordLoading, error: keywordError, fetchKeyword, deleteKeyword } = useKeyword();
  const { fetchHistory } = useKeywordRankHistory();

  useEffect(() => {
    if (keywordId) {
      fetchKeyword(keywordId);
      fetchHistory(keywordId);
    }
  }, [keywordId, fetchKeyword, fetchHistory]);

  const handleDelete = async () => {
    if (window.confirm('정말 이 키워드를 삭제하시겠습니까?')) {
      await deleteKeyword(keywordId);
      if (keyword?.place?.id) {
        navigate(`/places/${keyword.place.id}`);
      } else {
        navigate(-1);
      }
    }
  };

  if (isKeywordLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (keywordError || !keyword) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{keywordError || '키워드를 찾을 수 없습니다.'}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mb: 2, color: 'text.secondary' }}
        >
          뒤로가기
        </Button>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 800 }}>
                {keyword.keyword}
              </Typography>
              <Chip
                label={keyword.keyword_type.toUpperCase()}
                color="secondary"
                variant="outlined"
                size="small"
                sx={{ fontWeight: 600 }}
              />
            </Box>
            <Button
              startIcon={<StoreIcon />}
              onClick={() => navigate(`/places/${keyword.place.id}`)}
              sx={{ color: 'text.secondary', pl: 0, justifyContent: 'flex-start', '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}
            >
              {keyword.place.name}
            </Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              disabled
            >
              수정
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
            >
              삭제
            </Button>
          </Box>
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Card elevation={0} sx={{ border: '1px solid #e9ecef', borderRadius: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>키워드 정보</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">검색 쿼리</Typography>
                    <Typography variant="body1">{keyword.search_query || '-'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">맵 타입</Typography>
                    <Typography variant="body1">{keyword.current_map_type || '-'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">등록일</Typography>
                    <Typography variant="body1">
                      {new Date(keyword.created_at).toLocaleDateString()}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            <Card elevation={0} sx={{ border: '1px solid #e9ecef', borderRadius: 3, bgcolor: '#f8f9fa' }}>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="subtitle2" color="text.secondary">현재 순위</Typography>
                <Typography variant="h2" sx={{ fontWeight: 800, color: '#1a1a1a', my: 1 }}>
                  {keyword.current_rank ? `${keyword.current_rank}위` : '-'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                   마지막 확인: {keyword.last_checked_at ? new Date(keyword.last_checked_at).toLocaleString() : '-'}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              height: '100%',
              minHeight: 400,
              bgcolor: 'white',
              border: '1px solid #e9ecef',
              borderRadius: 3,
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
              순위 히스토리
            </Typography>
            <Box sx={{ 
              flex: 1, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              flexDirection: 'column',
              bgcolor: '#f8f9fa',
              borderRadius: 2,
              border: '1px dashed #ced4da'
            }}>
              <ShowChartIcon sx={{ fontSize: 64, color: '#adb5bd', mb: 2 }} />
              <Typography color="text.secondary" variant="h6">
                순위 히스토리 차트
              </Typography>
              <Typography variant="caption" color="text.disabled">
                (Chart Component Placeholder)
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}
